import { Box, IconButton, Typography } from '@material-ui/core'
import { Close, DeleteOutlined } from '@material-ui/icons'
import { Fragment } from 'react'
import { useFieldArray } from 'react-hook-form'

import Button from '@/components/button'
import InputText from '@/components/input-text'
import { useSaveHerbicideTreatment } from '@/hooks/mutations/use-save-herbicide-treatment'
import { HerbicideTreatment } from '@/services/get-herbicide-treatments'

import {
  SaveHerbicideTreatmentForm,
  useSaveHerbicideTreatmentForm,
} from '../../hooks/use-save-herbicide-treatment-form'
import { CurrencyInput } from '../currency-input'
import { HerbicideAutocomplete } from '../herbicide-autocomplete'
import { useStyles } from './styles'

export interface SaveHerbicideTreatmentProps {
  onClose: () => void
  isEditing?: boolean
  selectedHerbicideTreatment: HerbicideTreatment | null
}

export function SaveHerbicideTreatment({
  onClose,
  isEditing,
  selectedHerbicideTreatment,
}: SaveHerbicideTreatmentProps) {
  const classes = useStyles()

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useSaveHerbicideTreatmentForm({
    defaultValues: isEditing
      ? {
          treatment_name: selectedHerbicideTreatment?.treatment_name,
          herbicides: selectedHerbicideTreatment?.herbicides.map(
            (herbicide) => ({
              herbicide: {
                id: herbicide.id,
                herbicide_name: herbicide.herbicide_name,
                supplier: herbicide.supplier,
                registration_number: herbicide.registration_number,
              },
              dose: herbicide.herbicide_dose,
            }),
          ),
        }
      : undefined,
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'herbicides',
  })

  const { mutate, isLoading } = useSaveHerbicideTreatment({
    id: selectedHerbicideTreatment?.id,
  })

  function addNewHerbicide() {
    append({} as SaveHerbicideTreatmentForm['herbicides'][number])
  }

  function removeHerbicide(index: number) {
    remove(index)
  }

  function onSubmit(data: SaveHerbicideTreatmentForm) {
    mutate(
      {
        ...data,
        herbicides: data.herbicides.map((herbicide) => ({
          herbicide_id: herbicide.herbicide.id,
          herbicide_dose: herbicide.dose,
        })),
      },
      {
        onSuccess: () => onClose(),
      },
    )
  }

  return (
    <div className={classes.container}>
      <form className={classes.card} onSubmit={handleSubmit(onSubmit)}>
        <Box
          display={'flex'}
          alignItems={'center'}
          justifyContent={'space-between'}
        >
          <Typography variant='h3'>
            {isEditing
              ? 'Editar tratamento com defensivos'
              : 'Criar novo tratamento'}
          </Typography>
          <IconButton
            size='small'
            onClick={() => onClose()}
            aria-label='fechar'
            color='secondary'
            disabled={isLoading}
          >
            <Close />
          </IconButton>
        </Box>
        <div className={classes.inputsContainer}>
          <InputText
            id='treatment_name'
            label='Nome do tratamento'
            error={!!errors.treatment_name}
            helperText={errors.treatment_name?.message}
            {...register('treatment_name')}
          />
          <Box
            display={'grid'}
            gridGap={'1rem'}
            gridTemplateColumns={'1fr 1fr min-content'}
          >
            {fields
              .map((field, index) => {
                register(`herbicides.${index}.herbicide`)
                return field
              })
              .map((field, index) => (
                <Fragment key={field.id}>
                  <HerbicideAutocomplete control={control} index={index} />

                  <InputText
                    id={`herbicides.${field.id}.dose`}
                    type='number'
                    label='Dose (Concentração de produto não diluído em kg ou L/ha)'
                    error={!!errors.herbicides?.[index]?.dose}
                    helperText={errors.herbicides?.[index]?.dose?.message}
                    inputProps={{ step: 'any' }}
                    {...register(`herbicides.${index}.dose`, {
                      valueAsNumber: true,
                    })}
                  />
                  <IconButton
                    className={classes.iconButton}
                    size='small'
                    color='secondary'
                    aria-label={`Remover herbicida ${index}`}
                    onClick={() => removeHerbicide(index)}
                    disabled={fields.length <= 1}
                  >
                    <DeleteOutlined />
                  </IconButton>
                </Fragment>
              ))}
            <Button
              className={classes.button}
              outlined
              onClick={addNewHerbicide}
            >
              Adicionar outro herbicida
            </Button>

            <CurrencyInput
              label='Custo do tratamento:'
              error={errors.treatment_cost?.message}
              defaultValue={selectedHerbicideTreatment?.treatment_cost.toString()}
              {...register('treatment_cost')}
            />
          </Box>
        </div>
        <Box
          marginTop={'2.4rem'}
          display={'flex'}
          gridGap={'2rem'}
          justifyContent={'end'}
        >
          <Button
            className={classes.button}
            outlined
            onClick={() => onClose()}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button className={classes.button} type='submit' loading={isLoading}>
            Salvar tratamento
          </Button>
        </Box>
      </form>
    </div>
  )
}
